import { importSyncBundle, type SyncBundle } from "../platform/sync-bundle";

const SIGNAL_VERSION = 1;
const MAX_SYNC_BUNDLE_BYTES = 8 * 1024 * 1024;

type Signal = { v: 1; type: "offer" | "answer"; sdp: RTCSessionDescriptionInit };
type SyncMessage = { type: "sync-bundle"; bundle: string } | { type: "sync-ack"; count: number };

export class PeerSyncTransfer {
  private connection?: RTCPeerConnection;
  private channel?: RTCDataChannel;
  private ackWaiter?: { resolve: (count: number) => void; reject: (error: Error) => void; timer: number };

  constructor(private readonly onReceive: (bundle: SyncBundle) => void, private readonly onStatus: (message: string, error?: boolean) => void) {}

  async createOffer(stunUrl = ""): Promise<string> {
    this.reset();
    const connection = this.createConnection(stunUrl);
    this.channel = connection.createDataChannel("local-sync-bundle", { ordered: true });
    this.channel.onopen = () => this.onStatus("Sync sender channel open. Apply the receiver answer.");
    this.channel.onmessage = (event) => void this.handleMessage(event.data);
    this.channel.onerror = () => this.onStatus("Sync sender DataChannel error.", true);
    await connection.setLocalDescription(await connection.createOffer());
    await waitForIceGathering(connection);
    if (!connection.localDescription) throw new Error("Sync offer was not created.");
    return encodeSignal({ v: SIGNAL_VERSION, type: "offer", sdp: connection.localDescription });
  }

  async createAnswer(offerCode: string, stunUrl = ""): Promise<string> {
    this.reset();
    const offer = decodeSignal(offerCode);
    if (offer.type !== "offer") throw new Error("Expected a sync offer.");
    const connection = this.createConnection(stunUrl);
    const ready = new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error("Sync receiver channel did not arrive.")), 20_000);
      connection.ondatachannel = (event) => {
        window.clearTimeout(timer);
        this.channel = event.channel;
        this.channel.onopen = () => resolve();
        this.channel.onmessage = (message) => void this.handleMessage(message.data);
        this.channel.onerror = () => reject(new Error("Sync receiver DataChannel error."));
      };
    });
    await connection.setRemoteDescription(offer.sdp);
    await connection.setLocalDescription(await connection.createAnswer());
    await waitForIceGathering(connection);
    if (!connection.localDescription) throw new Error("Sync answer was not created.");
    void ready.catch((error) => this.onStatus(error.message, true));
    return encodeSignal({ v: SIGNAL_VERSION, type: "answer", sdp: connection.localDescription });
  }

  async applyAnswerAndSend(answerCode: string, bytes: Uint8Array): Promise<void> {
    if (!this.connection || !this.channel) throw new Error("Create a sync offer before applying an answer.");
    if (bytes.byteLength > MAX_SYNC_BUNDLE_BYTES) throw new Error("Sync bundle exceeds the 8 MiB transfer limit.");
    const answer = decodeSignal(answerCode);
    if (answer.type !== "answer") throw new Error("Expected a sync answer.");
    await this.connection.setRemoteDescription(answer.sdp);
    await waitForDataChannelOpen(this.channel);
    const bundle = new TextDecoder().decode(bytes);
    this.channel.send(JSON.stringify({ type: "sync-bundle", bundle } satisfies SyncMessage));
    const count = await this.waitForAck();
    this.onStatus(`Sent and acknowledged ${count} sync change(s).`);
  }

  dispose(): void {
    this.reset();
  }

  private createConnection(stunUrl: string): RTCPeerConnection {
    const connection = new RTCPeerConnection({ iceServers: stunUrl.trim() ? [{ urls: stunUrl.trim() }] : [] });
    this.connection = connection;
    connection.onconnectionstatechange = () => {
      if (connection.connectionState === "failed") this.onStatus("Sync peer connection failed.", true);
    };
    return connection;
  }

  private async handleMessage(data: unknown): Promise<void> {
    if (typeof data !== "string") throw new Error("Unexpected sync payload.");
    const message = JSON.parse(data) as SyncMessage;
    if (message.type === "sync-ack") {
      if (!this.ackWaiter) return;
      const waiter = this.ackWaiter;
      this.ackWaiter = undefined;
      window.clearTimeout(waiter.timer);
      waiter.resolve(message.count);
      return;
    }
    if (message.type !== "sync-bundle") throw new Error("Unknown sync message.");
    const bytes = new TextEncoder().encode(message.bundle);
    if (bytes.byteLength > MAX_SYNC_BUNDLE_BYTES) throw new Error("Incoming sync bundle exceeds the 8 MiB transfer limit.");
    const bundle = await importSyncBundle(bytes);
    this.onReceive(bundle);
    this.channel?.send(JSON.stringify({ type: "sync-ack", count: bundle.changes.length } satisfies SyncMessage));
    this.onStatus(`Received and validated ${bundle.changes.length} sync change(s). Download or apply them explicitly.`);
  }

  private waitForAck(timeoutMs = 30_000): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.ackWaiter = undefined;
        reject(new Error("Sync receiver did not acknowledge the bundle."));
      }, timeoutMs);
      this.ackWaiter = { resolve, reject, timer };
    });
  }

  private reset(): void {
    this.channel?.close();
    this.connection?.close();
    this.channel = undefined;
    this.connection = undefined;
    if (this.ackWaiter) {
      window.clearTimeout(this.ackWaiter.timer);
      this.ackWaiter.reject(new Error("Sync transfer session reset."));
      this.ackWaiter = undefined;
    }
  }
}

function encodeSignal(signal: Signal): string {
  const bytes = new TextEncoder().encode(JSON.stringify(signal));
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

function decodeSignal(code: string): Signal {
  const normalized = code.trim().replaceAll("-", "+").replaceAll("_", "/");
  const padded = normalized + "=".repeat((4 - normalized.length % 4) % 4);
  const binary = atob(padded);
  const signal = JSON.parse(new TextDecoder().decode(Uint8Array.from(binary, (char) => char.charCodeAt(0)))) as Signal;
  if (signal.v !== SIGNAL_VERSION || !signal.sdp?.type || !signal.sdp.sdp) throw new Error("Sync signal is invalid.");
  return signal;
}

async function waitForIceGathering(connection: RTCPeerConnection, timeoutMs = 20_000): Promise<void> {
  if (connection.iceGatheringState === "complete") return;
  await new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error("Sync ICE gathering timed out.")), timeoutMs);
    const handler = () => {
      if (connection.iceGatheringState === "complete") {
        window.clearTimeout(timer);
        connection.removeEventListener("icegatheringstatechange", handler);
        resolve();
      }
    };
    connection.addEventListener("icegatheringstatechange", handler);
  });
}

async function waitForDataChannelOpen(channel: RTCDataChannel, timeoutMs = 20_000): Promise<void> {
  if (channel.readyState === "open") return;
  await new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error("Sync DataChannel did not open.")), timeoutMs);
    channel.addEventListener("open", () => { window.clearTimeout(timer); resolve(); }, { once: true });
    channel.addEventListener("error", () => reject(new Error("Sync DataChannel failed.")), { once: true });
  });
}
